import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    MagnifyingGlass, IdentificationCard, WarningOctagon, CheckCircle,
    ShieldWarning, CurrencyDollar, MapPin, ClipboardText, Printer,
} from "@phosphor-icons/react";

export default function PlateLookup() {
    const { t } = useI18n();
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [types, setTypes] = useState([]);
    const [issueOpen, setIssueOpen] = useState(false);

    useEffect(() => {
        api.get("/violation-types").then((r) => setTypes(r.data.types || [])).catch(() => {});
    }, []);

    const search = async (e) => {
        e?.preventDefault?.();
        if (!query.trim()) return;
        setLoading(true); setResult(null);
        try {
            const { data } = await api.get(`/plates/lookup/${encodeURIComponent(query.trim().toUpperCase())}`);
            setResult(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("error"));
        }
        setLoading(false);
    };

    return (
        <div className="max-w-3xl mx-auto space-y-6" data-testid="plate-lookup-page">
            <div>
                <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· FIELD LOOKUP</div>
                <h1 className="text-3xl font-bold tracking-tight mt-1">{t("plateLookupTitle")}</h1>
                <p className="text-sm text-muted-foreground mt-1">{t("plateLookupHint")}</p>
            </div>

            <Card className="p-4 rounded-sm border-border">
                <form onSubmit={search} className="flex gap-2 items-end flex-wrap" data-testid="plate-lookup-form">
                    <div className="flex-1 min-w-[200px]">
                        <Label className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground">{t("plateNumber")}</Label>
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value.toUpperCase())}
                            placeholder="DEMO-GRN-001"
                            className="mono h-12 text-lg mt-1"
                            data-testid="plate-lookup-input"
                            autoFocus
                        />
                    </div>
                    <Button type="submit" disabled={loading || !query.trim()} className="h-12 rounded-sm bg-primary hover:bg-primary/90" data-testid="plate-lookup-submit">
                        <MagnifyingGlass size={18} weight="bold" className="me-1" />
                        {loading ? t("loading") : t("search")}
                    </Button>
                </form>
            </Card>

            {result && (
                <Card className="p-5 rounded-sm border-border space-y-5" data-testid="plate-lookup-result">
                    {result.found ? (
                        <>
                            <div className="flex items-center gap-3 flex-wrap">
                                <IdentificationCard size={28} weight="duotone" className="text-primary" />
                                <div>
                                    <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{t("plateNumber")}</div>
                                    <div className="mono text-2xl font-black">{result.plate.plate_number}</div>
                                </div>
                                <div className="ms-auto flex items-center gap-2">
                                    {result.plate.status === "active" ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-green-100 text-green-900 border border-green-400 text-xs uppercase tracking-widest" data-testid="lookup-status-active">
                                            <CheckCircle size={14} weight="fill" />{t("active")}
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-widest" data-testid="lookup-status-revoked">
                                            <ShieldWarning size={14} weight="fill" />{t("revoked")}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3 text-sm">
                                <Field label={t("ownerName")} value={result.owner?.full_name} testid="lookup-owner-name" />
                                <Field label={t("region")} value={result.plate.region} testid="lookup-region" />
                                <Field label={t("make")} value={result.owner?.vehicle?.make} />
                                <Field label={t("model")} value={result.owner?.vehicle?.model} />
                                <Field label={t("year")} value={result.owner?.vehicle?.year} />
                                <Field label={t("vin")} value={result.owner?.vehicle?.vin} mono />
                                <Field label={t("stickerValidUntil")} value={(result.plate.expires_at || "").slice(0, 10)} mono />
                                <Field label="KID" value={result.plate.kid} mono />
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center gap-2 text-amber-700 text-sm" data-testid="lookup-not-found">
                            <WarningOctagon size={18} weight="fill" />
                            {t("plateNotFound")}
                        </div>
                    )}

                    <div>
                        <div className="text-[10px] tracking-widest uppercase text-muted-foreground font-bold mb-2 flex items-center gap-2">
                            <WarningOctagon size={12} weight="fill" />{t("violationsHistory")} · {result.open_violations.length}
                        </div>
                        {result.open_violations.length === 0 ? (
                            <div className="text-xs text-muted-foreground py-2" data-testid="no-violations">{t("noViolations")}</div>
                        ) : (
                            <ul className="divide-y divide-border rounded-sm border border-border">
                                {result.open_violations.map((v) => (
                                    <li key={v.id} className="p-3 flex items-start justify-between gap-3 flex-wrap text-sm" data-testid={`violation-row-${v.id}`}>
                                        <div className="min-w-0">
                                            <div className="font-medium">{v.type_label}</div>
                                            <div className="text-[10px] text-muted-foreground mono">{new Date(v.issued_at).toLocaleString()} · {v.officer_email}</div>
                                            {v.location && <div className="text-xs text-muted-foreground mt-0.5">{v.location}</div>}
                                        </div>
                                        <div className="text-end shrink-0">
                                            <div className="mono font-bold">{v.amount} {v.currency}</div>
                                            <div className={`text-[10px] uppercase tracking-widest ${v.status === "paid" ? "text-green-700" : v.status === "dismissed" ? "text-muted-foreground" : "text-primary"}`}>{v.status}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <Button
                        onClick={() => setIssueOpen(true)}
                        className="w-full h-12 rounded-sm bg-primary hover:bg-primary/90"
                        data-testid="open-issue-violation"
                    >
                        <WarningOctagon size={18} weight="bold" className="me-1" />
                        {t("issueViolation")}
                    </Button>
                </Card>
            )}

            {issueOpen && result && (
                <IssueViolationDialog
                    open={issueOpen}
                    onClose={() => setIssueOpen(false)}
                    plateNumber={result.plate_number}
                    types={types}
                    onIssued={() => { setIssueOpen(false); search(); }}
                />
            )}
        </div>
    );
}

function Field({ label, value, mono, testid }) {
    return (
        <div>
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{label}</div>
            <div className={mono ? "mono" : ""} data-testid={testid}>{value || <span className="text-muted-foreground">—</span>}</div>
        </div>
    );
}

function IssueViolationDialog({ open, onClose, plateNumber, types, onIssued }) {
    const { t } = useI18n();
    const [typeCode, setTypeCode] = useState(types[0]?.code || "");
    const [amount, setAmount] = useState(types[0]?.fine ?? "");
    const [currency, setCurrency] = useState("USD");
    const [location, setLocation] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const t2 = types.find((x) => x.code === typeCode);
        if (t2) setAmount(t2.fine);
    }, [typeCode, types]);

    const doIssue = async (printNow) => {
        setBusy(true);
        try {
            const { data } = await api.post("/violations", {
                plate_number: plateNumber,
                type_code: typeCode,
                amount: Number(amount),
                currency, location, notes,
                print_now: !!printNow,
            });
            if (printNow) {
                if (data.printed) toast.success(t("violationPrintedOnSpot"));
                else toast.error(data.print_error || t("violationPrintFailed"));
            } else {
                toast.success(t("violationIssued"));
            }
            onIssued?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("error"));
        }
        setBusy(false);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="uppercase tracking-widest text-xs">{t("issueViolation")} — {plateNumber}</DialogTitle></DialogHeader>
                <form onSubmit={(e) => { e.preventDefault(); doIssue(false); }} className="space-y-3" data-testid="issue-violation-form">
                    <div>
                        <Label className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground">{t("violationType")}</Label>
                        <select
                            value={typeCode}
                            onChange={(e) => setTypeCode(e.target.value)}
                            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm mt-1"
                            data-testid="violation-type-select"
                            required
                        >
                            {types.map((v) => <option key={v.code} value={v.code}>{v.label} · {v.fine}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <div className="col-span-2">
                            <Label className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground"><CurrencyDollar size={12} className="inline me-1" />{t("fineAmount")}</Label>
                            <Input type="number" min="0" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="mono mt-1" data-testid="violation-amount" required />
                        </div>
                        <div>
                            <Label className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground">{t("currency")}</Label>
                            <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={6} className="mono mt-1" data-testid="violation-currency" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground"><MapPin size={12} className="inline me-1" />{t("location")}</Label>
                        <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Corner of Main & 3rd" className="mt-1" data-testid="violation-location" />
                    </div>
                    <div>
                        <Label className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground"><ClipboardText size={12} className="inline me-1" />{t("notes")}</Label>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full mt-1 rounded-md border border-input bg-background p-2 text-sm" data-testid="violation-notes" />
                    </div>
                    <div className="flex gap-2 justify-end flex-wrap">
                        <Button type="button" variant="ghost" onClick={onClose} data-testid="violation-cancel">{t("cancel")}</Button>
                        <Button type="button" onClick={() => doIssue(true)} disabled={busy} variant="outline" data-testid="violation-issue-print">
                            <Printer size={14} weight="bold" className="me-1" />{t("issueAndPrint")}
                        </Button>
                        <Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="violation-submit">
                            {busy ? t("loading") : t("issueViolation")}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
